import {
  JiraProjectIssueRow,
  ProjectCostCalculationResponse,
  ProjectCostIssueSummary,
} from '../../types';

export const COMMERCIAL_MARGIN_BASE_RATE_CENTS = 22485;

export const PROJECT_COST_COLUMN_IDS = {
  localCost: '__cost_configured_cents',
  saleValue: '__sale_value_cents',
  estimatedCost: '__estimated_cost_cents',
  ownHours: '__own_hours',
  thirdHours: '__third_party_hours',
  ownCost: '__own_cost_cents',
  thirdCost: '__third_party_cost_cents',
  issueCost: '__issue_cost_cents',
  commercialMargin: '__commercial_margin_cents',
  commercialMarginPct: '__commercial_margin_pct',
  saleMargin: '__sale_margin_cents',
  saleMarginPct: '__sale_margin_pct',
  postCostMarginPct: '__post_cost_margin_pct',
} as const;

export type IssueCostComputed = {
  saleValueCents: number;
  estimatedCostCents: number;
  ownHours: number;
  thirdHours: number;
  ownCostCents: number;
  thirdCostCents: number;
  issueCostCents: number;
  commercialMarginCents: number;
  commercialMarginPct: number | null;
  saleMarginCents: number;
  saleMarginPct: number | null;
  postCostMarginPct: number | null;
};

export const normalizeProjectKeyFromIssue = (value?: string | null): string => {
  const raw = String(value || '').trim().toUpperCase();
  if (!raw) return '';
  const split = raw.split('-');
  return split[0] || '';
};

export const buildJiraIssueByKey = (issues: JiraProjectIssueRow[]): Map<string, JiraProjectIssueRow> => {
  const map = new Map<string, JiraProjectIssueRow>();
  issues.forEach((issue) => {
    const issueKey = String(issue.key || '').trim().toUpperCase();
    if (issueKey) map.set(issueKey, issue);
  });
  return map;
};

export const buildIssueCostMapsFromAnalysis = (
  costAnalysis: ProjectCostCalculationResponse | null | undefined,
  jiraIssueByKey: Map<string, JiraProjectIssueRow>,
): {
  issueComputedMap: Record<string, IssueCostComputed>;
  projectCostMap: Record<string, number>;
  projectSingleIssueMap: Record<string, IssueCostComputed | null>;
} => {
  const issueComputedMap: Record<string, IssueCostComputed> = {};
  const projectCostMap: Record<string, number> = {};
  const projectSingleIssueMap: Record<string, IssueCostComputed | null> = {};

  (costAnalysis?.issues || []).forEach((item: ProjectCostIssueSummary) => {
    const issueKey = String(item.issue_key || '').trim().toUpperCase();
    const projectKey = String(item.project_key || '').trim().toUpperCase() || normalizeProjectKeyFromIssue(item.issue_key);
    const jiraIssue = jiraIssueByKey.get(issueKey);
    const saleValueCents = Number(item.revenue_cents || 0);
    const ownHours = Number(item.own_hours || 0);
    const thirdHours = Number(item.third_party_hours || 0);
    const issueCostCents = Number(item.cost_cents || 0);
    const ownCostCents = Number(item.own_cost_cents || 0);
    const thirdCostCents = Number(item.third_party_cost_cents || 0);
    const soldHours = Number(jiraIssue?.hh_vendido_seconds || 0) / 3600;
    const commercialMarginCents = soldHours > 0
      ? Math.round(soldHours * COMMERCIAL_MARGIN_BASE_RATE_CENTS)
      : 0;
    const estimatedCommercialCostCents = saleValueCents - commercialMarginCents;
    const commercialMarginPct = saleValueCents > 0 ? (commercialMarginCents / saleValueCents) * 100 : null;
    const saleMarginCents = saleValueCents - issueCostCents;
    const saleMarginPct = saleValueCents > 0 ? (saleMarginCents / saleValueCents) * 100 : null;

    const computed: IssueCostComputed = {
      saleValueCents,
      estimatedCostCents: estimatedCommercialCostCents,
      ownHours,
      thirdHours,
      ownCostCents,
      thirdCostCents,
      issueCostCents,
      commercialMarginCents,
      commercialMarginPct,
      saleMarginCents,
      saleMarginPct,
      postCostMarginPct: saleMarginPct,
    };

    if (projectKey) {
      projectCostMap[projectKey] = Number(projectCostMap[projectKey] || 0) + issueCostCents;
      if (!(projectKey in projectSingleIssueMap)) {
        projectSingleIssueMap[projectKey] = computed;
      } else if (projectSingleIssueMap[projectKey]) {
        projectSingleIssueMap[projectKey] = null;
      }
    }
    if (issueKey) {
      issueComputedMap[issueKey] = computed;
    }
  });

  return { issueComputedMap, projectCostMap, projectSingleIssueMap };
};

export const decorateIssueRowWithCostMaps = (
  row: JiraProjectIssueRow,
  issueComputedMap: Record<string, IssueCostComputed>,
  projectCostMap: Record<string, number>,
  projectSingleIssueMap?: Record<string, IssueCostComputed | null>,
): JiraProjectIssueRow => {
  const projectKey = String(row.project_key || '').trim().toUpperCase() || normalizeProjectKeyFromIssue(row.key);
  const issueKey = String(row.key || '').trim().toUpperCase();
  const computed = issueComputedMap[issueKey] || (projectKey ? (projectSingleIssueMap?.[projectKey] || undefined) : undefined);
  const ids = PROJECT_COST_COLUMN_IDS;
  const existing = row.extra_fields || {};

  const pickNumber = (columnId: string, value: number | undefined): number | null => {
    if (computed != null && value != null) return value;
    const fromRow = Number(existing[columnId]);
    return Number.isFinite(fromRow) ? fromRow : (value ?? null);
  };

  const pickNullableNumber = (columnId: string, value: number | null | undefined): number | null => {
    if (computed != null) return value ?? null;
    const fromRow = existing[columnId];
    if (fromRow == null) return value ?? null;
    const parsed = Number(fromRow);
    return Number.isFinite(parsed) ? parsed : (value ?? null);
  };

  const ownCostCents = pickNumber(ids.ownCost, computed?.ownCostCents) ?? 0;
  const thirdCostCents = pickNumber(ids.thirdCost, computed?.thirdCostCents) ?? 0;
  const ownHours = pickNumber(ids.ownHours, computed?.ownHours) ?? 0;
  const thirdHours = pickNumber(ids.thirdHours, computed?.thirdHours) ?? 0;
  const issueCostCents = pickNumber(ids.issueCost, computed?.issueCostCents) ?? 0;

  return {
    ...row,
    // KPIs do dashboard leem estes campos no topo da row (não só extra_fields).
    own_cost_cents: ownCostCents,
    third_party_cost_cents: thirdCostCents,
    own_hours: ownHours,
    third_party_hours: thirdHours,
    cost_cents: issueCostCents,
    extra_fields: {
      ...existing,
      [ids.localCost]: projectKey
        ? (projectCostMap[projectKey] ?? pickNumber(ids.localCost, undefined))
        : pickNumber(ids.localCost, undefined),
      [ids.saleValue]: pickNumber(ids.saleValue, computed?.saleValueCents),
      [ids.estimatedCost]: pickNumber(ids.estimatedCost, computed?.estimatedCostCents),
      [ids.ownHours]: ownHours,
      [ids.thirdHours]: thirdHours,
      [ids.ownCost]: ownCostCents,
      [ids.thirdCost]: thirdCostCents,
      [ids.issueCost]: issueCostCents,
      [ids.commercialMargin]: pickNumber(ids.commercialMargin, computed?.commercialMarginCents),
      [ids.commercialMarginPct]: pickNullableNumber(ids.commercialMarginPct, computed?.commercialMarginPct ?? null),
      [ids.saleMargin]: pickNumber(ids.saleMargin, computed?.saleMarginCents),
      [ids.saleMarginPct]: pickNullableNumber(ids.saleMarginPct, computed?.saleMarginPct ?? null),
      [ids.postCostMarginPct]: pickNullableNumber(ids.postCostMarginPct, computed?.postCostMarginPct ?? null),
    },
  };
};
